<template>
  <div
    class="card h-100 border-border/10 shadow-card bg-white round-xl hover:shadow-md"
  >
    <div class="card-body">
      <div class="d-flex flex-column align-middle">
        <!-- reviewiewer Info -->
        <div class="d-flex align-items-center gap-y-3">
          <div
            class="round-circle bg-primary d-flex align-items-center justify-content-center"
            style="width: 40px; height: 40px"
          >
            <span class="small text-white">{{ review.initials }}</span>
          </div>
          <div class="flex-grow-1 min-w-0">
            <div class="d-flex align-items-center justify-content-between">
              <div>
                <h3 class="font-semibold text-gray mb-0">{{ review.name }}</h3>
                <div class="d-flex align-items-center gap-y-3 mt-1">
                  <div class="d-flex align-items-center gap-y-1">
                    <span
                      v-for="s in review.stars"
                      :key="s"
                      class="text-warning"
                      style="font-size: 16px"
                      >&#9733;</span
                    >
                  </div>
                  <span class="text-sm text-gray">{{ review.timeAgo }}</span>
                </div>
              </div>
              <div class="d-flex align-items-center gap-y-2">
                <span
                  class="badge bg-primary text-primary round-xl bg-opacity-20"
                >
                  {{ review.platform }}
                </span>
              </div>
            </div>
          </div>
        </div>

        <!-- reviewiew Text -->
        <p class="mt-3 text-sm text-gray line-clamp-4">{{ review.text }}</p>

        <!-- Reply Section (only show if replyText exists) -->
        <div
          v-if="review.replyText"
          class="mt-3 pl-3"
          style="border-left: 1px solid #c6c6c7 !important"
        >
          <div class="bg-gray round-xl border-border/20 p-3">
            <div class="d-flex align-items-center justify-content-between mb-2">
              <div class="d-flex align-items-center gap-y-2">
                <i class="uil uil-comment-alt text-gray"></i>
                <span class="text-xs font-medium text-gray font-weight-bold">
                  Your Reply:
                </span>
              </div>
              <div class="d-flex align-items-center gap-y-2">
                <span class="text-xs text-success"
                  >Posted {{ review.repliedAt }}</span
                >
                <button class="btn btn-sm px-2 py-1 text-xs text-gray"
                  >Edit</button
                >
              </div>
            </div>
            <p class="text-xs text-gray leading-relaxed mb-0">{{
              review.replyText
            }}</p>
          </div>
        </div>

        <!-- Reply Buttons -->
        <div class="mt-4 pt-4 border-top space-y-3">
          <div class="space-y-2">
            <div class="d-flex align-items-start gap-y-3">
              <span class="text-xs font-medium text-gray min-w-fit"
                >Reply:</span
              >
              <div class="d-flex align-items-start gap-y-3">
                <!-- Reply Posted -->
                <div class="d-flex flex-column align-items-center">
                  <button
                    class="btn btn-sm text-gray round-xl border h-7 text-xs px-3 d-flex align-items-center gap-y-2"
                  >
                    <i class="uil uil-message"></i>
                    <span class="ms-1">Reply Posted</span>
                    <span
                      v-if="review.reply"
                      class="text-xs text-gray ms-2"
                      >&#10003;</span
                    >
                  </button>
                  <small
                    v-if="review.reply"
                    class="text-xs text-success mt-1"
                    >Updated: {{ review.shared.facebook }}</small
                  >
                </div>

                <!-- Re-write -->
                <div class="d-flex flex-column align-items-start">
                  <button
                    class="btn btn-sm text-gray round-xl border h-7 text-xs px-3 d-flex align-items-center gap-y-2"
                  >
                    <i class="uil uil-restart"></i>
                    <span class="ms-1">Re-write</span>
                  </button>
                  <small
                    v-if="review.shared?.instagram"
                    class="text-xs text-muted mt-1"
                    >Instagram: {{ review.shared.instagram }}</small
                  >
                </div>

                <!-- Update -->
                <div class="d-flex flex-column align-items-start">
                  <button
                    class="btn btn-sm text-gray round-xl border h-7 text-xs px-3 d-flex align-items-center gap-y-2"
                  >
                    <i class="uil uil-upload"></i>
                    <span class="ms-1">Update</span>
                  </button>
                  <small
                    v-if="review.shared?.google"
                    class="text-xs text-muted mt-1"
                    >Google: {{ review.shared.google }}</small
                  >
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Share Buttons -->
        <div class="mt-4 pt-4 border-top space-y-3">
          <div class="space-y-2">
            <div class="d-flex align-items-start gap-y-3">
              <span class="text-xs font-medium text-gray min-w-fit"
                >Share:</span
              >
              <div class="d-flex align-items-start gap-y-3">
                <!-- Facebook -->
                <div class="d-flex flex-column align-items-center">
                  <button
                    class="btn btn-sm text-gray round-xl border h-7 text-xs px-3 d-flex align-items-center gap-y-2"
                  >
                    <i class="uil uil-facebook-f"></i>
                    <span class="ms-1">Facebook</span>
                    <span
                      v-if="review.shared?.facebook"
                      class="text-xs text-gray ms-2"
                      >&#10003;</span
                    >
                  </button>
                  <small
                    v-if="review.shared?.facebook"
                    class="text-xs text-success mt-1"
                    >Facebook: {{ review.shared.facebook }}</small
                  >
                </div>

                <!-- Instagram -->
                <div class="d-flex flex-column align-items-start">
                  <button
                    class="btn btn-sm text-gray round-xl border h-7 text-xs px-3 d-flex align-items-center gap-y-2"
                  >
                    <i class="uil uil-instagram"></i>
                    <span class="ms-1">Instagram</span>
                  </button>
                  <small
                    v-if="review.shared?.instagram"
                    class="text-xs text-muted mt-1"
                    >Instagram: {{ review.shared.instagram }}</small
                  >
                </div>

                <!-- Google -->
                <div class="d-flex flex-column align-items-start">
                  <button
                    class="btn btn-sm text-gray round-xl border h-7 text-xs px-3 d-flex align-items-center gap-y-2"
                  >
                    <i class="uil uil-google"></i>
                    <span class="ms-1">Google</span>
                  </button>
                  <small
                    v-if="review.shared?.google"
                    class="text-xs text-muted mt-1"
                    >Google: {{ review.shared.google }}</small
                  >
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'ReviewCard',
  props: {
    review: {
      type: Object,
      required: true,
    },
  },
}
</script>
